import { useCallback, useEffect, useState } from "react";

import { Edit as EditIcon } from "@mui/icons-material";
import { Button, Chip, IconButton, Stack } from "@mui/material";
import Grid from "@mui/material/Grid";
import { DataGrid } from "@mui/x-data-grid";
import { PageContainer } from "@/features/ui";

import { useAuth } from "@/features/authentication";
import {
  AddExpenseCategoryFormDialog,
  EditExpenseCategoryFormDialog,
} from "@/features/expense-categories";
import { PermissionEnum } from "@/features/permissions";
import { apiClient, statusColors, statusLabels, getErrorMessage } from "@/features/shared";
import { SearchTextField, useSnackbar } from "@/features/ui";
import { useSearchFilter, useServerPagination } from "@/hooks";

export const ExpenseCategories = () => {
  const { hasPermission } = useAuth();
  const { showSnackbar } = useSnackbar();
  const [isAddDialogOpen, setIsAddDialogOpen] = useState(false);
  const [expenseCategoryId, setExpenseCategoryId] = useState(null);
  const [expenseCategories, setExpenseCategories] = useState([]);
  const [loading, setLoading] = useState(false);

  const { setRowCount, paginationParams, dataGridPaginationProps, resetParams } =
    useServerPagination();
  const { keyword, searchProps } = useSearchFilter({ resetParams });

  const loadExpenseCategories = useCallback(async () => {
    setLoading(true);
    try {
      const {
        data: { data, pageInfo },
      } = await apiClient.get("expense-categories", {
        params: {
          ...paginationParams,
          ...(keyword && { search: keyword }),
          withChartOfAccount: true,
        },
      });
      setExpenseCategories(data);
      setRowCount(pageInfo.totalItems);
    } catch (error) {
      showSnackbar(getErrorMessage(error, "Gagal memuat kategori biaya"));
    } finally {
      setLoading(false);
    }
  }, [paginationParams, keyword, setRowCount, showSnackbar]);

  useEffect(() => {
    loadExpenseCategories();
  }, [loadExpenseCategories]);

  const toolbarContent = hasPermission(PermissionEnum.CREATE_EXPENSE_CATEGORIES) && (
        <Button
          variant="contained"
          onClick={() => setIsAddDialogOpen(true)}
        >
          Tambah
        </Button>
      );

  return (
    <PageContainer maxWidth="xl" breadcrumbs={[]} slotProps={{ header: { slotProps: { toolbar: { children: toolbarContent } } } }}>
      <Stack spacing={3}>
        <Grid container sx={{ justifyContent: "flex-end" }}>
          <Grid size={{ xs: 12, md: 6, lg: 4 }}>
            <SearchTextField
              fullWidth
              placeholder="Cari nama kategori"
              {...searchProps}
            />
          </Grid>
        </Grid>
        <DataGrid
          rows={expenseCategories}
          loading={loading}
          getRowHeight={() => "auto"}
          columns={[
            {
              field: "name",
              headerName: "Nama",
              flex: 3,
              sortable: false,
            },
            {
              field: "chartOfAccount",
              headerName: "Akun",
              flex: 2,
              sortable: false,
              valueGetter: (value) =>
                value ? `${value.code} - ${value.name}` : "-",
            },
            {
              field: "status",
              headerName: "Status",
              flex: 1,
              sortable: false,
              renderCell: ({ row }) => (
                <Chip
                  label={statusLabels[row.status]}
                  color={statusColors[row.status]}
                />
              ),
            },
            ...(hasPermission(PermissionEnum.UPDATE_EXPENSE_CATEGORIES)
              ? [
                  {
                    field: "actions",
                    type: "actions",
                    headerName: "Aksi",
                    getActions: (params) => [
                      <IconButton
                        key="edit"
                        onClick={() => setExpenseCategoryId(params.id)}
                        size="small"
                      >
                        <EditIcon />
                      </IconButton>,
                    ],
                  },
                ]
              : []),
          ]}
          {...dataGridPaginationProps}
          disableRowSelectionOnClick
        />
        <AddExpenseCategoryFormDialog
          isOpen={isAddDialogOpen}
          onClose={() => setIsAddDialogOpen(false)}
          onSubmitted={loadExpenseCategories}
        />
        <EditExpenseCategoryFormDialog
          expenseCategoryId={expenseCategoryId}
          isOpen={!!expenseCategoryId}
          onClose={() => setExpenseCategoryId(null)}
          onSubmitted={loadExpenseCategories}
        />
      </Stack>
    </PageContainer>
  );
};
